import { ZipWriter } from '../../bulk/zip';
import { canvasToBlob } from './download';
import { encodeGif, GIF_MIN_DELAY_MS, type GifFrame } from './gif';
import { AnimationSequencer, frameCountFor, type AnimationSettings } from './animate';
import { createCanvas, get2d } from '../text/rasterize';
import type { LoadedFont } from '../fonts/fontLoader';
import type { StudioDocument } from '../../store/studioStore';

/**
 * Turning a frame sequence into a file.
 *
 * Each format wants the frames in a different shape, and the difference is not cosmetic:
 *
 *  - **PNG sequence** encodes and discards each frame as it arrives, so its memory cost
 *    is one frame regardless of length. It is the only format with no quality or timing
 *    compromise at all, which is why it is the recommended route into a real video tool.
 *  - **GIF** needs every frame in memory at once, because the palette is derived from
 *    all of them together.
 *  - **WebM/MP4** goes through `MediaRecorder`, which timestamps frames by wall clock.
 *    That means the frames have to be *played* at the target rate, and a render slower
 *    than the frame interval would stretch the video. So the frames are captured first
 *    and replayed afterwards — recording is then a real-time operation of exactly the
 *    requested duration, independent of how long the rendering took.
 *
 * The buffered formats therefore carry a memory estimate, and the caller warns rather
 * than letting the tab die.
 */
export type AnimationFormat = 'gif' | 'video' | 'pngzip';

export interface VideoCodec {
  id: string;
  mimeType: string;
  label: string;
  extension: string;
}

const CANDIDATE_CODECS: VideoCodec[] = [
  { id: 'webm-vp9', mimeType: 'video/webm;codecs=vp9', label: 'WebM · VP9', extension: 'webm' },
  { id: 'webm-vp8', mimeType: 'video/webm;codecs=vp8', label: 'WebM · VP8', extension: 'webm' },
  { id: 'webm', mimeType: 'video/webm', label: 'WebM', extension: 'webm' },
  { id: 'mp4-h264', mimeType: 'video/mp4;codecs=avc1.42E01E', label: 'MP4 · H.264', extension: 'mp4' },
  { id: 'mp4', mimeType: 'video/mp4', label: 'MP4', extension: 'mp4' },
];

/**
 * Which video containers this browser can actually write.
 *
 * Probed rather than assumed: Chrome and Firefox write WebM, Safari writes MP4, and
 * offering a format the browser will refuse produces an empty file with no explanation.
 */
export function availableCodecs(): VideoCodec[] {
  if (typeof MediaRecorder === 'undefined') return [];
  const seen = new Set<string>();
  const out: VideoCodec[] = [];
  for (const codec of CANDIDATE_CODECS) {
    if (!MediaRecorder.isTypeSupported(codec.mimeType)) continue;
    // One entry per container: the first supported codec for each is the best one.
    if (seen.has(codec.extension)) continue;
    seen.add(codec.extension);
    out.push(codec);
  }
  return out;
}

/** Bytes held at once for the buffered formats. */
export function estimateMemory(settings: AnimationSettings, doc: StudioDocument, format: AnimationFormat): number {
  if (format === 'pngzip') return 0;
  const width = Math.round(doc.canvas.width * settings.scale);
  const height = Math.round(doc.canvas.height * settings.scale);
  return width * height * 4 * frameCountFor(settings);
}

export interface AnimationExportOptions {
  doc: StudioDocument;
  font: LoadedFont;
  settings: AnimationSettings;
  format: AnimationFormat;
  /** Video only. */
  codec?: VideoCodec;
  bitrateMbps?: number;
  /** GIF only. */
  gifColors?: number;
  gifDither?: boolean;
  onProgress?: (progress: number, phase: string) => void;
  signal?: AbortSignal;
}

export interface AnimationExportResult {
  blob: Blob;
  extension: string;
  frames: number;
  width: number;
  height: number;
  ms: number;
  /** Anything the user should know about the result, in plain German. */
  notes: string[];
}

export async function exportAnimation(options: AnimationExportOptions): Promise<AnimationExportResult> {
  const t0 = performance.now();
  const { doc, font, settings, format, signal } = options;
  const notes: string[] = [];

  const sequencer = new AnimationSequencer({ doc, font, settings, signal });
  options.onProgress?.(0, 'Aufbau wird vermessen');
  await sequencer.prepare((p) => options.onProgress?.(p * 0.15, 'Aufbau wird vermessen'));
  if (signal?.aborted) throw new Error('Abgebrochen.');

  const { width, height } = sequencer;
  const renderShare = format === 'video' ? 0.6 : format === 'gif' ? 0.55 : 0.9;
  const progressFor = (i: number, total: number): number =>
    0.15 + (renderShare - 0.15) * ((i + 1) / total);

  // ── PNG sequence: streamed, one frame in memory ────────────────────────────
  if (format === 'pngzip') {
    const zip = new ZipWriter();
    const digits = String(sequencer.frameCount).length;
    let count = 0;

    for await (const frame of sequencer.frames()) {
      const blob = await canvasToBlob(frame.canvas, 'png', 1);
      await zip.add(`frame_${String(frame.index).padStart(digits, '0')}.png`, blob);
      count += 1;
      options.onProgress?.(progressFor(frame.index, frame.total), `Frame ${frame.index + 1}/${frame.total}`);
    }
    if (signal?.aborted) throw new Error('Abgebrochen.');

    await zip.add(
      'sequence.txt',
      [
        `Frames: ${count}`,
        `Bildrate: ${settings.fps} fps`,
        `Auflösung: ${width}×${height}`,
        '',
        'Zusammensetzen mit ffmpeg:',
        `ffmpeg -framerate ${settings.fps} -i frame_%0${digits}d.png -c:v libx264 -pix_fmt yuv420p out.mp4`,
      ].join('\n'),
    );

    notes.push('Verlustfrei — die Sequenz lässt sich in jedem Videowerkzeug zusammensetzen.');
    return { blob: zip.finish(), extension: 'zip', frames: count, width, height, ms: performance.now() - t0, notes };
  }

  // ── Buffered formats: capture every frame first ────────────────────────────
  const scratch = createCanvas(width, height);
  const scratchCtx = get2d(scratch);
  const captured: ImageData[] = [];

  for await (const frame of sequencer.frames()) {
    scratchCtx.setTransform(1, 0, 0, 1, 0, 0);
    scratchCtx.globalAlpha = 1;
    scratchCtx.globalCompositeOperation = 'copy';
    scratchCtx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
    captured.push(scratchCtx.getImageData(0, 0, width, height));
    options.onProgress?.(progressFor(frame.index, frame.total), `Frame ${frame.index + 1}/${frame.total}`);
  }
  if (signal?.aborted) throw new Error('Abgebrochen.');
  if (!captured.length) throw new Error('Keine Frames erzeugt.');

  if (format === 'gif') {
    const delayMs = 1000 / settings.fps;
    if (delayMs < GIF_MIN_DELAY_MS) {
      notes.push(
        `GIF speichert Verzögerungen in 10-ms-Schritten; unter ${Math.round(1000 / GIF_MIN_DELAY_MS)} fps ` +
          'spielen viele Betrachter langsamer ab als eingestellt.',
      );
    }
    const frames: GifFrame[] = captured.map((data) => ({ data: data.data, delayMs }));
    const blob = encodeGif(frames, {
      width,
      height,
      colors: options.gifColors ?? 256,
      dither: options.gifDither !== false,
      loopCount: 0,
      onProgress: (p) => options.onProgress?.(renderShare + (1 - renderShare) * p, 'GIF wird kodiert'),
    });
    return {
      blob,
      extension: 'gif',
      frames: captured.length,
      width,
      height,
      ms: performance.now() - t0,
      notes,
    };
  }

  // ── Video ──────────────────────────────────────────────────────────────────
  const codec = options.codec ?? availableCodecs()[0];
  if (!codec) throw new Error('Dieser Browser kann kein Video aufzeichnen. PNG-Sequenz oder GIF verwenden.');

  const blob = await recordFrames(captured, {
    width,
    height,
    fps: settings.fps,
    mimeType: codec.mimeType,
    bitsPerSecond: Math.round((options.bitrateMbps ?? 12) * 1_000_000),
    onProgress: (p) => options.onProgress?.(renderShare + (1 - renderShare) * p, 'Video wird aufgezeichnet'),
    signal,
  });

  notes.push('Aufzeichnung läuft in Echtzeit — die Dauer der Aufnahme entspricht der Länge des Videos.');
  if (codec.extension === 'webm') {
    notes.push('WebM spielt in Browsern und modernen Schnittprogrammen; für Instagram o. ä. besser die PNG-Sequenz.');
  }

  return {
    blob,
    extension: codec.extension,
    frames: captured.length,
    width,
    height,
    ms: performance.now() - t0,
    notes,
  };
}

interface RecordOptions {
  width: number;
  height: number;
  fps: number;
  mimeType: string;
  bitsPerSecond: number;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

/**
 * Plays pre-rendered frames into a `MediaRecorder` at the target rate.
 *
 * `captureStream(0)` puts frame timing under our control: nothing is captured until
 * `requestFrame()` is called, so the recorded stream has exactly the frames we push and
 * no duplicates from the compositor. The pacing loop corrects against a fixed schedule
 * rather than sleeping a constant interval, so timer jitter does not accumulate into a
 * video that runs slow.
 */
async function recordFrames(frames: readonly ImageData[], options: RecordOptions): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = options.width;
  canvas.height = options.height;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('2D-Kontext nicht verfügbar.');

  const stream = canvas.captureStream(0);
  const track = stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
  if (!track) throw new Error('Canvas-Aufzeichnung nicht verfügbar.');

  const recorder = new MediaRecorder(stream, {
    mimeType: options.mimeType,
    videoBitsPerSecond: options.bitsPerSecond,
  });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  const stopped = new Promise<void>((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = () => reject(new Error('Die Aufzeichnung ist fehlgeschlagen.'));
  });

  recorder.start();
  const interval = 1000 / options.fps;
  const start = performance.now();

  for (let i = 0; i < frames.length; i++) {
    if (options.signal?.aborted) break;
    ctx.putImageData(frames[i], 0, 0);
    track.requestFrame();
    options.onProgress?.((i + 1) / frames.length);

    // Absolute schedule: wait until frame i+1 is due, not "one interval from now".
    const due = start + (i + 1) * interval;
    const wait = due - performance.now();
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
  }

  // The last frame needs to be on screen for its full duration before the recorder
  // stops, otherwise it is dropped from the file.
  await new Promise((resolve) => setTimeout(resolve, interval));
  recorder.stop();
  await stopped;
  track.stop();

  if (!chunks.length) throw new Error('Die Aufzeichnung hat keine Daten geliefert.');
  return new Blob(chunks, { type: options.mimeType.split(';')[0] });
}
